

import { SectionCard } from "./SectionCard";
import { VendorDetailRow, ServiceDetailRow, fmt } from "./types";

interface VendorAllocationsProps {
  vendorDetail: VendorDetailRow[];
  serviceDetail: ServiceDetailRow[];
}

export function VendorAllocations({ vendorDetail, serviceDetail }: VendorAllocationsProps) {
  const hasVendors = vendorDetail && vendorDetail.length > 0;
  const hasServices = serviceDetail && serviceDetail.length > 0;
  const totalItems = (vendorDetail?.length || 0) + (serviceDetail?.length || 0);

  // Calculate totals
  const serviceTotal = (serviceDetail || []).reduce((sum, s) => sum + s.revenue, 0);
  const serviceCost = (serviceDetail || []).reduce((sum, s) => sum + s.cost, 0);
  const vendorTotal = (vendorDetail || []).reduce((sum, v) => sum + (v.billed ?? 0), 0);
  const vendorCost = (vendorDetail || []).reduce((sum, v) => sum + v.total_amount, 0);

  const grandTotalBilled = serviceTotal + vendorTotal;
  const grandTotalCost = serviceCost + vendorCost;
  const grandTotalProfit = grandTotalBilled - grandTotalCost;

  if (!hasVendors && !hasServices) {
    return null;
  }

  return (
    <SectionCard
      title={`Item Allocations (${totalItems} items)`}
      icon={
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      }
    >
      {/* Compact Summary Stats */}
      <div className="flex items-center gap-6 px-4 py-3 bg-stone-50 border-b border-stone-200 text-sm mb-4 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-stone-500">Total:</span>
          <span className="font-bold text-stone-800">{fmt(grandTotalBilled)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-stone-500">Cost:</span>
          <span className="font-bold text-rose-600">{fmt(grandTotalCost)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-stone-500">Profit:</span>
          <span className={`font-bold ${grandTotalProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {fmt(grandTotalProfit)}
          </span>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
        <div className="min-w-[700px] px-4 sm:px-0">
          <div className="rounded-xl border border-stone-100 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider w-16">
                    Type
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider w-10">
                    Qty
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider">
                    Billed
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider">
                    Profit
                  </th>
                  <th className="text-center px-3 py-2.5 font-semibold text-stone-500 uppercase tracking-wider w-20">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {/* Services */}
                {hasServices && serviceDetail.map((service, index) => {
                  const profit = service.revenue - service.cost;
                  return (
                    <tr key={`service-${index}`} className="hover:bg-stone-50/50 border-b border-stone-50">
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-violet-100 text-violet-700" title="Service">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-stone-800 text-xs">{service.name}</p>
                      </td>
                      <td className="px-3 py-2.5 text-right text-stone-600">{service.quantity}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-stone-800">
                        {fmt(service.revenue)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-rose-500">
                        {fmt(service.cost)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`font-medium text-xs ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {fmt(profit)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="text-xs text-emerald-600 font-semibold">✓ Active</span>
                      </td>
                    </tr>
                  );
                })}
                {/* Vendors */}
                {hasVendors && vendorDetail.map((vendor, index) => {
                  const cost = vendor.total_amount;
                  const profit = vendor.profit ?? (vendor.billed ?? 0) - cost;
                  return (
                    <tr key={`vendor-${index}`} className="hover:bg-stone-50/50 border-b border-stone-50">
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-100 text-blue-700" title="Vendor">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-stone-800 text-xs">{vendor.vendor_name}</p>
                        {vendor.description && <p className="text-[10px] text-stone-400 truncate">{vendor.description}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-right text-stone-600">{vendor.quantity ?? "-"}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-stone-800">
                        {fmt(vendor.billed ?? 0)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-rose-500">
                        {fmt(cost)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`font-medium text-xs ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {fmt(profit)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          vendor.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                          vendor.status === 'draft' ? 'bg-stone-100 text-stone-600' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {vendor.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-stone-50 border-t-2 border-stone-200 font-bold">
                  <td className="px-3 py-2.5 text-stone-800 text-xs" colSpan={3}>
                    Totals
                  </td>
                  <td className="px-3 py-2.5 text-right text-stone-800 text-xs">
                    {fmt(grandTotalBilled)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-rose-600 text-xs">
                    {fmt(grandTotalCost)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs">
                    <span className={grandTotalProfit >= 0 ? "text-emerald-700" : "text-red-700"}>
                      {fmt(grandTotalProfit)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-stone-100">
        {/* Services */}
        {hasServices && serviceDetail.map((service, index) => {
          const profit = service.revenue - service.cost;
          return (
            <div key={`mobile-service-${index}`} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-violet-100 text-violet-700">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-800 text-sm truncate">{service.name}</p>
                  <p className="text-xs text-stone-400 mt-1">Qty: {service.quantity}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Price</p>
                  <p className="text-sm font-bold text-stone-800">{fmt(service.revenue)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Cost</p>
                  <p className="text-sm font-bold text-rose-500">{fmt(service.cost)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Net</p>
                  <p className={`text-sm font-bold ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {fmt(profit)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        {/* Vendors */}
        {hasVendors && vendorDetail.map((vendor, index) => {
          return (
            <div key={`mobile-vendor-${index}`} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-100 text-blue-700">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-800 text-sm truncate">{vendor.vendor_name}</p>
                  {vendor.description && <p className="text-xs text-stone-500">{vendor.description}</p>}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Cost</p>
                  <p className="text-sm font-bold text-stone-800">{fmt(vendor.total_amount)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Paid</p>
                  <p className="text-sm font-bold text-emerald-600">{fmt(vendor.paid)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-400 uppercase mb-0.5">Owed</p>
                  <p className={`text-sm font-bold ${vendor.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {fmt(vendor.balance)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        {/* Mobile Totals */}
        <div className="px-4 py-3 bg-stone-50">
          <p className="text-xs font-semibold text-stone-600 mb-2">Grand Totals</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] text-stone-400 uppercase mb-0.5">Billed</p>
              <p className="text-sm font-bold text-stone-800">{fmt(grandTotalBilled)}</p>
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase mb-0.5">Cost</p>
              <p className="text-sm font-bold text-rose-600">{fmt(grandTotalCost)}</p>
            </div>
            <div>
              <p className="text-[10px] text-stone-400 uppercase mb-0.5">Net</p>
              <p className={`text-sm font-bold ${grandTotalProfit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                {fmt(grandTotalProfit)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
